import { API_ENDPOINTS } from '../endpoints';
import { downloadFileResponse, getAuthHeaders, handleResponse } from '../client';

export type OpportunityLinkedIpdVO = {
  projectKey: string;
  workItemId: number;
  projectCode?: string;
  name?: string;
};

export type OpportunityLinkedIpcVO = {
  projectId: number;
  code?: string;
  name?: string;
  status?: string;
  currentScheduleId?: number | null;
};

export type OpportunityAttachmentVO = {
  id?: number;
  opportunityId?: number;
  fileId?: number;
  fileName: string;
  /** 落库原始路径/URL */
  fileUrl?: string;
  /** 可下载地址（CDN） */
  downloadUrl?: string;
  fileSize?: number;
  contentType?: string;
};

export type OpportunityAttachmentItem = {
  fileId?: number;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  contentType?: string;
};

/** 对齐 OAK Dropzone：{BASE_API}/Upload/UploadFiles */
const UPLOAD_FILES_URL =
  ((import.meta as any).env?.VITE_UPLOAD_FILES_URL as string | undefined)?.trim()
  || 'https://gatetest.shjinjia.com.cn/api/Upload/UploadFiles';

/** 与后端一致：相对 File 路径拼 CDN，供上传后立刻下载。 */
export function resolveAttachmentDownloadUrl(fileUrl?: string | null): string {
  const raw = (fileUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const envCdn = ((import.meta as any).env?.VITE_CDN_BASE_URL as string | undefined)?.trim();
  const cdn = (envCdn
    || (/gatetest|upyuntest/i.test(UPLOAD_FILES_URL)
      ? 'https://cdn.upyuntest.mallimg.shjinjia.com.cn'
      : 'https://cdn.upyun.mallimg.shjinjia.com.cn')).replace(/\/$/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${cdn}${path}`;
}

export type OpportunityVO = {
  id: number;
  name: string;
  brand: string;
  productLine: string;
  category: string;
  productType: string;
  grade: string;
  plannedSkuCount?: string;
  estimatedInboundDate?: string | null;
  suggestedBriefDate?: string | null;
  moldType: string;
  needCert: string;
  sourceType: string;
  remark?: string;
  status: string;
  createTime?: string;
  updateTime?: string;
  attachmentCount?: number;
  attachments?: OpportunityAttachmentVO[];
  linkedIpds?: OpportunityLinkedIpdVO[];
  linkedIpcs?: OpportunityLinkedIpcVO[];
};

export type OpportunitySaveDTO = {
  id?: number;
  name: string;
  brand: string;
  productLine: string;
  category: string;
  productType: string;
  grade: string;
  plannedSkuCount?: string;
  estimatedInboundDate?: string | null;
  moldType: string;
  needCert: string;
  sourceType: string;
  remark?: string;
  attachments?: OpportunityAttachmentItem[];
};

export type OpportunityPaged = {
  list: OpportunityVO[];
  total: number;
  pageNum: number;
  pageSize: number;
};

export type BriefCycleConfigVO = {
  id: number;
  configJson: string;
};

export type PlanVersionListItem = {
  id: number;
  brand: string;
  planYear: number;
  coverYears: string;
  state: string;
  itemCount: number;
  updateTime: string;
};

export type PlanProjectItem = {
  id: number;
  name: string;
  brand?: string;
  productLine: string;
  category?: string;
  productType?: string;
  targetInboundDate?: string | null;
  grade: string;
  plannedSkuCount?: string;
  suggestedBriefDate?: string | null;
  moldType?: string;
  needCert?: string;
  sourceType?: string;
  remark?: string;
  sourceOpportunityId: number;
  /** 与机会池「状态」同口径 */
  status?: string;
  stage?: string;
  delayMonths?: number;
  scheduleTargetDate?: string | null;
  actualInboundDate?: string | null;
  attachmentCount?: number;
  attachments?: OpportunityAttachmentVO[];
  isNew?: boolean;
  matchedWorkItemIds?: number[];
  linkedIpds?: OpportunityLinkedIpdVO[];
  linkedIpcs?: OpportunityLinkedIpcVO[];
};

export type PlanVersionDetail = {
  id: number;
  brand: string;
  planYear: number;
  coverYears: string;
  state: string;
  items: PlanProjectItem[];
};

export type PlanBoardVO = {
  inProgress: number;
  completed: number;
  terminated: number;
  notStarted: number;
  delayed: number;
  outOfPlan: number;
  nodes: {
    planProjectId: number;
    workItemId?: number;
    name: string;
    productLine: string;
    category?: string;
    grade: string;
    stage: string;
    delayMonths: number;
    commitDate?: string | null;
    scheduleTargetDate?: string | null;
    actualInboundDate?: string | null;
    suggestedBriefDate?: string | null;
    moldType?: string;
    needCert?: string;
    axisMonth?: string;
    isNew?: boolean;
    linkedIpds?: OpportunityLinkedIpdVO[];
    linkedIpcs?: OpportunityLinkedIpcVO[];
  }[];
};

export const opportunityApi = {
  getList: async (params?: {
    keyword?: string;
    brand?: string;
    productLine?: string;
    productType?: string;
    status?: string;
    grade?: string;
    inboundDateFrom?: string;
    inboundDateTo?: string;
    pageNum?: number;
    pageSize?: number;
  }): Promise<OpportunityPaged> => {
    const q = new URLSearchParams();
    if (params?.keyword) q.append('Keyword', params.keyword);
    if (params?.brand) q.append('Brand', params.brand);
    if (params?.productLine) q.append('ProductLine', params.productLine);
    if (params?.productType) q.append('ProductType', params.productType);
    if (params?.status) q.append('Status', params.status);
    if (params?.grade) q.append('Grade', params.grade);
    if (params?.inboundDateFrom) q.append('InboundDateFrom', params.inboundDateFrom);
    if (params?.inboundDateTo) q.append('InboundDateTo', params.inboundDateTo);
    q.append('PageNum', String(params?.pageNum ?? 1));
    q.append('PageSize', String(params?.pageSize ?? 20));
    const response = await fetch(`${API_ENDPOINTS.OPPORTUNITY.LIST}?${q}`, { headers: getAuthHeaders() });
    return handleResponse<OpportunityPaged>(response);
  },
  exportList: async (params?: {
    keyword?: string;
    brand?: string;
    productLine?: string;
    productType?: string;
    status?: string;
    grade?: string;
    inboundDateFrom?: string;
    inboundDateTo?: string;
  }): Promise<void> => {
    const q = new URLSearchParams();
    if (params?.keyword) q.append('Keyword', params.keyword);
    if (params?.brand) q.append('Brand', params.brand);
    if (params?.productLine) q.append('ProductLine', params.productLine);
    if (params?.productType) q.append('ProductType', params.productType);
    if (params?.status) q.append('Status', params.status);
    if (params?.grade) q.append('Grade', params.grade);
    if (params?.inboundDateFrom) q.append('InboundDateFrom', params.inboundDateFrom);
    if (params?.inboundDateTo) q.append('InboundDateTo', params.inboundDateTo);
    const response = await fetch(`${API_ENDPOINTS.OPPORTUNITY.EXPORT}?${q}`, { headers: getAuthHeaders() });
    await downloadFileResponse(response, '机会池.xlsx');
  },
  exportDetail: async (id: number): Promise<void> => {
    const response = await fetch(API_ENDPOINTS.OPPORTUNITY.EXPORT_DETAIL(id), { headers: getAuthHeaders() });
    await downloadFileResponse(response, `机会详情_${id}.xlsx`);
  },
  create: async (data: OpportunitySaveDTO) => {
    const response = await fetch(API_ENDPOINTS.OPPORTUNITY.CREATE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<OpportunityVO>(response);
  },
  update: async (data: OpportunitySaveDTO) => {
    const response = await fetch(API_ENDPOINTS.OPPORTUNITY.UPDATE, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<OpportunityVO>(response);
  },
  delete: async (id: number) => {
    const response = await fetch(API_ENDPOINTS.OPPORTUNITY.DELETE(id), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse(response);
  },
  syncMatch: async () => {
    const response = await fetch(API_ENDPOINTS.OPPORTUNITY.SYNC_MATCH, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ updated: number }>(response);
  },
  /**
   * 模仿 OAK UploadFile/Dropzone：直传 `VITE_UPLOAD_FILES_URL`，
   * 响应为 `{ File: 相对路径 }`；列表下载地址仍由后端拼 CDN。
   */
  uploadToFileSys: async (file: File): Promise<OpportunityAttachmentItem> => {
    const localToken = localStorage.getItem('access_token');
    const raw = (localToken && localToken.trim()
      ? localToken
      : ((import.meta as any).env?.VITE_DEV_TEST_TOKEN || '')).trim();
    const headers: HeadersInit = {};
    if (raw) {
      headers.Authorization = /^Bearer\s+/i.test(raw) ? raw : `Bearer ${raw}`;
    }

    const form = new FormData();
    form.append('file', file, file.name);
    const response = await fetch(UPLOAD_FILES_URL, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!response.ok) {
      throw new Error(`上传失败 HTTP ${response.status}`);
    }
    const data = await response.json();
    if (data.res_status_code !== undefined && String(data.res_status_code) !== '0') {
      throw new Error(data.res_message || '文件上传失败');
    }
    // OAK：JSON.parse(xhr.response).File；也兼容 res_content 包装
    const content = data.res_content ?? data.data ?? data;
    const relativePath = String(content?.File ?? content?.file ?? '').trim();
    if (!relativePath) throw new Error('上传接口未返回 File 路径');
    return {
      fileName: String(file.name || content?.file_name || ''),
      fileUrl: relativePath,
      fileSize: file.size || 0,
      contentType: file.type || '',
    };
  },
};

export const briefCycleApi = {
  get: async () => {
    const response = await fetch(API_ENDPOINTS.BRIEF_CYCLE.GET, { headers: getAuthHeaders() });
    return handleResponse<BriefCycleConfigVO>(response);
  },
  save: async (configJson: string) => {
    const response = await fetch(API_ENDPOINTS.BRIEF_CYCLE.SAVE, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ configJson }),
    });
    return handleResponse<BriefCycleConfigVO>(response);
  },
  trial: async (body: {
    estimatedInboundDate?: string | null;
    brand?: string;
    category?: string;
    productType?: string;
    grade?: string;
    moldType?: string;
    needCert?: string;
    sourceType?: string;
    configJson?: string;
  }) => {
    const response = await fetch(API_ENDPOINTS.BRIEF_CYCLE.TRIAL, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return handleResponse<{ months: number; label: string; ruleId?: string | null; suggestedBriefDate?: string | null; error?: string | null }>(response);
  },
};

export const planVersionApi = {
  getList: async () => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.LIST, { headers: getAuthHeaders() });
    return handleResponse<PlanVersionListItem[]>(response);
  },
  getDetail: async (id: number) => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.DETAIL(id), { headers: getAuthHeaders() });
    return handleResponse<PlanVersionDetail>(response);
  },
  getBoard: async (id: number) => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.BOARD(id), { headers: getAuthHeaders() });
    return handleResponse<PlanBoardVO>(response);
  },
  exportLaunchPlan: async (id: number): Promise<void> => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.EXPORT(id), { headers: getAuthHeaders() });
    await downloadFileResponse(response, `LaunchPlan_${id}.xlsx`);
  },
  create: async (data: { brand: string; planYear: number; items: Partial<PlanProjectItem>[] }) => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.CREATE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PlanVersionDetail>(response);
  },
  update: async (data: { id: number; items: Partial<PlanProjectItem>[] }) => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.UPDATE, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PlanVersionDetail>(response);
  },
  publish: async (id: number) => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.PUBLISH(id), {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    await handleResponse(response);
  },
  bring: async (planVersionId: number, opportunityIds: number[]) => {
    const response = await fetch(API_ENDPOINTS.PLAN_VERSION.BRING, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ planVersionId, opportunityIds }),
    });
    return handleResponse<PlanVersionDetail>(response);
  },
};
